import { cn } from '@/lib/utils';
import type { ComponentProps, PointerEvent, ReactNode } from 'react';

const CrossDecor = ({ position }: { position: 'top-start' | 'bottom-end' }) => (
  <svg
    aria-hidden
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1"
    strokeLinecap="round"
    className={cn(
      'pointer-events-none absolute z-10 size-3.5 shrink-0 text-muted-foreground/50',
      position === 'top-start' &&
        'start-0 top-0 -translate-y-1/2 ltr:-translate-x-1/2 rtl:translate-x-1/2',
      position === 'bottom-end' &&
        'bottom-0 end-0 translate-y-1/2 ltr:translate-x-1/2 rtl:-translate-x-1/2',
    )}
  >
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </svg>
);

type SpotlightCardProps = ComponentProps<'div'> & {
  showCrosshair?: boolean;
};

export function SpotlightCard({
  className,
  children,
  showCrosshair = true,
  onPointerMove,
  ...props
}: SpotlightCardProps) {
  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - rect.top}px`);
    onPointerMove?.(e);
  };

  return (
    <div
      onPointerMove={handlePointerMove}
      className={cn(
        'group relative flex h-full flex-col overflow-hidden bg-background',
        'bg-[radial-gradient(55%_90%_at_20%_0%,hsl(var(--foreground)/0.04),transparent)]',
        className,
      )}
      {...props}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100 bg-[radial-gradient(circle_220px_at_var(--mx)_var(--my),hsl(var(--foreground)/0.07),transparent_72%)]"
      />
      <div aria-hidden className="pointer-events-none absolute -inset-y-4 -start-px w-px bg-border/80" />
      <div aria-hidden className="pointer-events-none absolute -inset-y-4 -end-px w-px bg-border/80" />
      <div aria-hidden className="pointer-events-none absolute -inset-x-4 -top-px h-px bg-border/80" />
      <div aria-hidden className="pointer-events-none absolute -inset-x-4 -bottom-px h-px bg-border/80" />
      {showCrosshair ? (
        <>
          <CrossDecor position="top-start" />
          <CrossDecor position="bottom-end" />
        </>
      ) : null}
      {children}
    </div>
  );
}

type PremiumSurfaceProps = ComponentProps<'div'> & {
  glow?: boolean;
};

export function PremiumSurface({ className, children, glow = false, ...props }: PremiumSurfaceProps) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border border-border/60 bg-background/80 backdrop-blur-xl',
        glow && 'shadow-[0_24px_80px_-32px_hsl(var(--foreground)/0.18)]',
        className,
      )}
      {...props}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--foreground)/0.06),transparent)]"
      />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

export function PremiumDivider({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn(
        'h-3 w-full border-x border-border/60 bg-[repeating-linear-gradient(-45deg,hsl(var(--border)),hsl(var(--border))_1px,transparent_1px,transparent_7px)]',
        className,
      )}
    />
  );
}

export function PremiumStepPill({
  step,
  label,
  className,
}: {
  step: string;
  label: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-4 rounded-2xl border border-border/50 bg-secondary/30 px-5 py-4 transition-colors duration-300 hover:border-border hover:bg-secondary/50',
        className,
      )}
    >
      <span className="font-display text-[10px] tracking-[0.25em] text-muted-foreground pt-0.5">{step}</span>
      <p className="font-body text-sm text-foreground leading-snug">{label}</p>
    </div>
  );
}

export function PremiumFieldLabel({
  children,
  active,
  htmlFor,
}: {
  children: ReactNode;
  active?: boolean;
  htmlFor?: string;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        'font-body text-[11px] tracking-[0.2em] uppercase block mb-3 transition-all duration-500',
        active ? 'text-foreground translate-x-1' : 'text-muted-foreground',
      )}
    >
      {children}
    </label>
  );
}
